import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import api from "../../../api/api";

const statusColor = (status) => {
  const normalized = (status || "").toLowerCase();
  if (normalized.includes("closed") || normalized.includes("completed"))
    return "success";
  if (normalized.includes("confirm") || normalized.includes("ready"))
    return "warning";
  return "default";
};

// Текущая фаза заказа по флагам прогресса (см. AVITO_FULL_CYCLE_PLAN v2)
const getPhase = (row) => {
  if (row.returned_at) {
    return { num: 5, label: "Возвращён", color: "default", action: "repeat" };
  }
  if (row.pickup_completed) {
    return { num: 4, label: "Выдан", color: "success", action: "return" };
  }
  if (row.confirmed) {
    return { num: 3, label: "Ждёт выдачи", color: "warning", action: "deliver" };
  }
  return { num: 1, label: "Новый / ждёт оплаты", color: "default", action: "confirm" };
};

// Описание каждой кнопки-действия для tooltip
const ACTION_HINTS = {
  confirm:
    "Фаза 1. Подтвердить заказ вручную (обычно выполняется автоматически после оплаты).",
  cnc:
    "Фаза 1–2. Отправить в Авито адрес и окно бронирования самовывоза (CNC).",
  pin:
    "Фаза 2. Повторно отправить клиенту PIN, адрес ПВЗ и ссылку на оферту (отправляются автоматически после оплаты).",
  deliver:
    "Фаза 3. Выдать товар: проверить код клиента из Авито и подтвердить выдачу.",
  return:
    "Фаза 4. Зафиксировать возврат товара (состояние + фото).",
  repeat:
    "Фаза 5. Перевести клиента в прямой Telegram-канал для повторных заказов.",
};

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("ru-RU");
  } catch {
    return String(value);
  }
};

export default function AvitoOrdersPage() {
  const actionPending = useRef(false);
  const requestVersion = useRef(0);
  const deliverAttempt = useRef(0);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [pickupResult, setPickupResult] = useState(null);
  const [cncDialogOpen, setCncDialogOpen] = useState(false);
  const [cncForm, setCncForm] = useState({
    address: "",
    booking_hours: 48,
    details: "",
  });

  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnForm, setReturnForm] = useState({
    condition: "",
    comment: "",
    photos: "",
  });

  // --- Диалог выдачи с проверкой кода ---
  const [deliverDialogOpen, setDeliverDialogOpen] = useState(false);
  const [deliverOrder, setDeliverOrder] = useState(null);
  const [deliverCode, setDeliverCode] = useState("");
  const [deliverCodeVerified, setDeliverCodeVerified] = useState(false);
  const [deliverLoading, setDeliverLoading] = useState(false);

  const resetMessages = () => {
    setError("");
    setSuccess("");
  };

  const loadOrders = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    try {
      const response = await api.get("/avito-orders", {
        params: { limit: 200 },
      });
      if (version !== requestVersion.current) return;
      setOrders(response.data.items || []);
      setError("");
    } catch (e) {
      if (version === requestVersion.current) setError(e.response?.data?.detail || "Не удалось загрузить Avito-заказы");
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
    return () => { requestVersion.current += 1; deliverAttempt.current += 1; };
  }, [loadOrders]);

  const callAction = async (fn, successText) => {
    if (actionPending.current) return false;
    actionPending.current = true;
    resetMessages();
    try {
      const response = await fn();
      const warning = response?.data?.avito_warning;
      setSuccess(warning ? `${successText}. Предупреждение: ${warning}` : successText);
      await loadOrders();
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Операция завершилась с ошибкой');
      return false;
    } finally {
      actionPending.current = false;
    }
  };

  const syncOrders = () =>
    callAction(async () => {
      await api.post("/avito-orders/sync", { page: 1, limit: 20 });
    }, "Синхронизация заказов выполнена");

  const runReminders = () =>
    callAction(async () => {
      await api.post("/avito-orders/reminders/run");
    }, "Напоминания по Avito-заказам отправлены");

  const openCncDialog = (orderId) => {
    resetMessages();
    setCncForm({ address: '', booking_hours: 48, details: '' });
    setSelectedOrderId(orderId);
    setCncDialogOpen(true);
  };

  const submitCnc = async () => {
    if (!selectedOrderId) return;
    const hours = Number(cncForm.booking_hours);
    if (!cncForm.address.trim() || !Number.isInteger(hours) || hours < 1 || hours > 720) {
      setError('Укажите адрес и целое окно бронирования от 1 до 720 часов');
      return;
    }
    const saved = await callAction(() => api.post(`/avito-orders/${selectedOrderId}/prepare-cnc`, {
      ...cncForm, address: cncForm.address.trim(), booking_hours: hours,
    }), "Данные CNC отправлены");
    if (saved) setCncDialogOpen(false);
  };

  const openReturnDialog = (orderId) => {
    resetMessages();
    setReturnForm({ condition: '', comment: '', photos: '' });
    setSelectedOrderId(orderId);
    setReturnDialogOpen(true);
  };

  const openDeliverDialog = (row) => {
    deliverAttempt.current += 1;
    setDeliverLoading(false);
    setDeliverOrder(row);
    setDeliverCode("");
    setDeliverCodeVerified(false);
    setDeliverDialogOpen(true);
    resetMessages();
  };

  const closeDeliverDialog = () => {
    deliverAttempt.current += 1;
    setDeliverDialogOpen(false);
  };

  const checkDeliverCode = async () => {
    if (!deliverOrder || !deliverCode.trim() || deliverLoading) return;
    const attempt = deliverAttempt.current;
    setDeliverLoading(true);
    resetMessages();
    try {
      await api.post(
        `/avito-orders/${deliverOrder.avito_order_id}/check-code`,
        {
          confirm_code: deliverCode,
          parcel_id: deliverOrder.parcel_id,
        },
      );
      if (attempt === deliverAttempt.current) setDeliverCodeVerified(true);
    } catch (e) {
      if (attempt === deliverAttempt.current) setError(e.response?.data?.detail || "Код не прошёл проверку");
    } finally {
      if (attempt === deliverAttempt.current) setDeliverLoading(false);
    }
  };

  const submitDeliver = async () => {
    if (!deliverOrder || !deliverCodeVerified) return;
    const saved = await callAction(() => api.post(`/avito-orders/${deliverOrder.avito_order_id}/receive`), "Выдача подтверждена");
    if (saved) closeDeliverDialog();
  };

  const submitReturn = async () => {
    if (!selectedOrderId) return;

    const photos = returnForm.photos
      .split("\n")
      .map((p) => p.trim())
      .filter(Boolean);

    const saved = await callAction(async () => {
      await api.post(`/avito-orders/${selectedOrderId}/return`, {
        condition: returnForm.condition || null,
        comment: returnForm.comment || null,
        photos,
      });
    }, "Возврат зафиксирован");

    if (saved) setReturnDialogOpen(false);
  };

  const rows = useMemo(() => orders || [], [orders]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack className="erp-section-heading"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight={700}>
          Avito Заказы
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadOrders}
          >
            Обновить
          </Button>
          <Button variant="contained" onClick={syncOrders}>
            Синхронизировать
          </Button>
          <Button variant="contained" color="warning" onClick={runReminders}>
            Напоминания 2/1 день
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {pickupResult && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<QrCode2Icon />}>
          PIN: {pickupResult.pin} | Deep-link: {pickupResult.telegram_deep_link}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box className="erp-table-scroll"><Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Заказ</TableCell>
              <TableCell>Клиент</TableCell>
              <TableCell>Товар</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>CRM</TableCell>
              <TableCell>Возврат</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.avito_order_id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={700}>
                    {row.avito_order_id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    MP: {row.marketplace_id || "-"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {row.buyer_name || "-"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.buyer_phone || "-"}
                  </Typography>
                </TableCell>
                <TableCell>{row.item_title || "-"}</TableCell>
                <TableCell>
                  <Stack spacing={0.5} alignItems="flex-start">
                    <Chip
                      size="small"
                      label={row.status || "-"}
                      color={statusColor(row.status)}
                    />
                    {(() => {
                      const phase = getPhase(row);
                      return (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={phase.color}
                          label={`Фаза ${phase.num}: ${phase.label}`}
                        />
                      );
                    })()}
                  </Stack>
                </TableCell>
                <TableCell>
                  {row.crm_deal_id ? (
                    <Chip
                      size="small"
                      color="success"
                      label={`Сделка #${row.crm_deal_id}`}
                    />
                  ) : (
                    <Chip size="small" label="Нет" />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" display="block">
                    План: {formatDate(row.expected_return_at)}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Факт: {formatDate(row.returned_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {(() => {
                    const phase = getPhase(row);
                    const isActive = (action) => phase.action === action;
                    return (
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Tooltip title={ACTION_HINTS.confirm} arrow>
                          <span>
                            <Button
                              size="small"
                              variant={isActive("confirm") ? "contained" : "outlined"}
                              disabled={row.confirmed}
                              onClick={() =>
                                callAction(
                                  () =>
                                    api.post(
                                      `/avito-orders/${row.avito_order_id}/confirm`,
                                    ),
                                  "Заказ подтвержден",
                                )
                              }
                            >
                              1. Подтвердить
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={ACTION_HINTS.cnc} arrow>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openCncDialog(row.avito_order_id)}
                            >
                              CNC-детали
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={ACTION_HINTS.pin} arrow>
                          <span>
                            <Button
                              size="small"
                              variant={isActive("deliver") || isActive("confirm") ? "outlined" : "outlined"}
                              onClick={() =>
                                callAction(async () => {
                                  const response = await api.post(
                                    `/avito-orders/${row.avito_order_id}/pickup-access`,
                                  );
                                  setPickupResult(response.data);
                                }, "PIN и ссылка для клиента сформированы")
                              }
                            >
                              2. PIN + оферта
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={ACTION_HINTS.deliver} arrow>
                          <span>
                            <Button
                              size="small"
                              color="success"
                              variant={isActive("deliver") ? "contained" : "outlined"}
                              disabled={!row.confirmed || row.pickup_completed}
                              onClick={() => openDeliverDialog(row)}
                            >
                              3. Выдать
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={ACTION_HINTS.return} arrow>
                          <span>
                            <Button
                              size="small"
                              color="warning"
                              variant={isActive("return") ? "contained" : "outlined"}
                              disabled={!row.pickup_completed || !!row.returned_at}
                              onClick={() => openReturnDialog(row.avito_order_id)}
                            >
                              4. Возврат
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={ACTION_HINTS.repeat} arrow>
                          <span>
                            <Button
                              size="small"
                              color="secondary"
                              variant={isActive("repeat") ? "contained" : "outlined"}
                              disabled={!row.returned_at || row.repeat_client_ready}
                              onClick={() =>
                                callAction(
                                  () =>
                                    api.post(
                                      `/avito-orders/${row.avito_order_id}/repeat-client/activate`,
                                    ),
                                  "Повторный клиент активирован",
                                )
                              }
                            >
                              5. Повт. клиент
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    );
                  })()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></Box>
      )}

      <Dialog
        open={cncDialogOpen}
        onClose={() => setCncDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Подготовка CNC</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Адрес выдачи"
              value={cncForm.address}
              onChange={(e) =>
                setCncForm((prev) => ({ ...prev, address: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Окно бронирования (часы)"
              type="number"
              value={cncForm.booking_hours}
              onChange={(e) =>
                setCncForm((prev) => ({
                  ...prev,
                  booking_hours: e.target.value,
                }))
              }
              fullWidth
            />
            <TextField
              label="Комментарий"
              value={cncForm.details}
              onChange={(e) =>
                setCncForm((prev) => ({ ...prev, details: e.target.value }))
              }
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCncDialogOpen(false)}>Отмена</Button>
          <Button onClick={submitCnc} variant="contained">
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог выдачи с проверкой кода */}
      <Dialog
        open={deliverDialogOpen}
        onClose={closeDeliverDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Выдача товара</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <Box sx={{ p: 1.5, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Клиент:</strong> {deliverOrder?.buyer_name || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Товар:</strong> {deliverOrder?.item_title || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Сумма:</strong>{" "}
                {deliverOrder?.total_price
                  ? `${deliverOrder.total_price} ₽`
                  : "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Заказ Авито:</strong>{" "}
                {deliverOrder?.marketplace_id ||
                  deliverOrder?.avito_order_id ||
                  "-"}
              </Typography>
            </Box>

            {!deliverCodeVerified ? (
              <>
                <TextField
                  label="Код от клиента (из Авито)"
                  value={deliverCode}
                  onChange={(e) => setDeliverCode(e.target.value)}
                  inputProps={{ maxLength: 4 }}
                  placeholder="4 цифры"
                  fullWidth
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && checkDeliverCode()}
                />
                <Button
                  variant="contained"
                  onClick={checkDeliverCode}
                  disabled={deliverLoading || deliverCode.length < 4}
                >
                  {deliverLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Проверить код"
                  )}
                </Button>
              </>
            ) : (
              <Alert severity="success">
                Код подтверждён — можно выдавать товар
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeliverDialog}>Отмена</Button>
          <Button
            onClick={submitDeliver}
            variant="contained"
            color="success"
            disabled={!deliverCodeVerified}
          >
            Подтвердить выдачу
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={returnDialogOpen}
        onClose={() => setReturnDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Фиксация возврата</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Состояние при возврате"
              value={returnForm.condition}
              onChange={(e) =>
                setReturnForm((prev) => ({
                  ...prev,
                  condition: e.target.value,
                }))
              }
              fullWidth
            />
            <TextField
              label="Комментарий"
              value={returnForm.comment}
              onChange={(e) =>
                setReturnForm((prev) => ({ ...prev, comment: e.target.value }))
              }
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              label="Фото URL (каждый с новой строки)"
              value={returnForm.photos}
              onChange={(e) =>
                setReturnForm((prev) => ({ ...prev, photos: e.target.value }))
              }
              multiline
              rows={4}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnDialogOpen(false)}>Отмена</Button>
          <Button onClick={submitReturn} variant="contained" color="warning">
            Зафиксировать
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
